import { Button } from "@chakra-ui/react"
import Pathicon from "@pathwright/ux/src/components/pathicon/Pathicon"
import React from "react"
import { ColorStyle } from "./base-style-defaults"
import {
  black,
  ChooseColorsWrapper,
  ColorInput,
  ColorRow,
  InputLabel,
  offBlack,
  StyleItem,
  white
} from "./ui"
import { clearCustomColors } from "./utils"

type CustomColorsProps = {
  color: string
  backgroundColor: string
  onSubmitColors: (colors: ColorStyle) => void
  onClearCustomColors: () => void
  onGoBack: () => void
  hasCustomColors: boolean
}

const getDefaultColorState = (props: CustomColorsProps) => {
  return {
    color: props.color || black,
    backgroundColor: props.backgroundColor || white
  }
}

const CustomColors = (props: CustomColorsProps) => {
  const { onSubmitColors, onClearCustomColors, onGoBack, hasCustomColors } =
    props

  const defaultState = getDefaultColorState(props)

  const [color, setColor] = React.useState(defaultState.color)
  const [backgroundColor, setBackgroundColor] = React.useState(
    defaultState.backgroundColor
  )
  const [clearingCustom, setClearingCustom] = React.useState(false)

  const handleChangeColor =
    (colorType: keyof ColorStyle) => (value: string) => {
      if (colorType === "color") {
        setColor(value)
      } else {
        setBackgroundColor(value)
      }
    }

  // Apply custom colors (and close color picker)
  const handleSubmitColors = () => onSubmitColors({ color, backgroundColor })

  const handleToggleClear = (toggle: boolean) => () => setClearingCustom(toggle)

  // Remove custom colors in local storage and close custom colors panel
  const handleClearCustom = () => {
    setClearingCustom(false)
    clearCustomColors()
    onClearCustomColors()
  }

  const colorSquareStyles = {
    width: "40px",
    height: "30px",
    marginLeft: "5px",
    borderRadius: "7px",
    padding: "5px 10px"
  }

  return (
    <ChooseColorsWrapper>
      <Button
        // @ts-expect-error: custom platform button variant
        variant="link"
        color={offBlack}
        onClick={onGoBack}
        borderBottom="none"
        fontSize="1em"
        fontWeight="normal"
        gap={2}
      >
        <Pathicon icon="arrow-left" />
        Back
      </Button>
      <StyleItem
        style={{
          margin: "1rem 0",
          padding: "0",
          height: "100px",
          border: "solid 1px #efefef",
          color,
          backgroundColor,
          cursor: "auto",
          borderRadius: "7px"
        }}
      >
        This is a preview
      </StyleItem>
      <InputLabel>Text Color</InputLabel>
      <ColorRow>
        <ColorInput
          type="text"
          onChange={handleChangeColor("color")}
          placeholder="Add a text color"
          value={color}
        />
        <StyleItem
          className="BlocksStyles__StyleItem"
          $selected={color === white || color === "#fff" || color === "white"}
          $applyHoverShadows={true}
          style={{
            ...colorSquareStyles,
            backgroundColor: white,
            border: "1px solid lightgray",
            boxShadow:
              color === white || color === "#fff" || color === "white"
                ? "inset 0 0 0 2px white, 0 0 0 2px rgba(100,100,100)"
                : ""
          }}
          onClick={() => handleChangeColor("color")(white)}
        />
        <StyleItem
          className="BlocksStyles__StyleItem"
          $selected={color === black || color === "#000" || color === "black"}
          $applyHoverShadows={true}
          style={{
            ...colorSquareStyles,
            backgroundColor: black,
            boxShadow:
              color === black || color === "#000" || color === "black"
                ? "inset 0 0 0 2px white, 0 0 0 2px rgba(100,100,100)"
                : ""
          }}
          onClick={() => handleChangeColor("color")(black)}
        />
      </ColorRow>
      <InputLabel>Background Color</InputLabel>
      <ColorRow>
        <ColorInput
          type="text"
          onChange={handleChangeColor("backgroundColor")}
          placeholder="Add a background color"
          value={backgroundColor}
        />
        <StyleItem
          className="BlocksStyles__StyleItem"
          $selected={
            backgroundColor === white ||
            backgroundColor === "#fff" ||
            backgroundColor === "white"
          }
          $applyHoverShadows={true}
          style={{
            ...colorSquareStyles,
            backgroundColor: white,
            border: "1px solid lightgray",
            boxShadow:
              backgroundColor === white ||
              backgroundColor === "#fff" ||
              backgroundColor === "white"
                ? "inset 0 0 0 2px white, 0 0 0 2px rgba(100,100,100)"
                : ""
          }}
          onClick={() => handleChangeColor("backgroundColor")(white)}
        />
        <StyleItem
          className="BlocksStyles__StyleItem"
          $selected={
            backgroundColor === black ||
            backgroundColor === "#000" ||
            backgroundColor === "black"
          }
          $applyHoverShadows={true}
          style={{
            ...colorSquareStyles,
            backgroundColor: black,
            boxShadow:
              backgroundColor === black ||
              backgroundColor === "#000" ||
              backgroundColor === "black"
                ? "inset 0 0 0 2px white, 0 0 0 2px rgba(100,100,100)"
                : ""
          }}
          onClick={() => handleChangeColor("backgroundColor")(black)}
        />
      </ColorRow>
      {clearingCustom ? (
        <ColorRow>
          Are you sure?
          <div>
            <Button
              // @ts-expect-error: custom platform button variant
              variant="legacy.default"
              mr="5px"
              onClick={handleClearCustom}
            >
              Yes
            </Button>
            <Button
              // @ts-expect-error: custom platform button variant
              variant="link"
              color={offBlack}
              onClick={handleToggleClear(false)}
            >
              Cancel
            </Button>
          </div>
        </ColorRow>
      ) : (
        <ColorRow>
          <Button
            // @ts-expect-error: custom platform button variant
            variant="legacy.default"
            mr="5px"
            onClick={handleSubmitColors}
          >
            Apply Changes
          </Button>
          {hasCustomColors && (
            <Button
              // @ts-expect-error: custom platform button variant
              variant="link"
              color={offBlack}
              textDecoration="underline"
              onClick={handleToggleClear(true)}
            >
              Clear Colors
            </Button>
          )}
        </ColorRow>
      )}
    </ChooseColorsWrapper>
  )
}

export default CustomColors
